import { TextVariants } from '@/interfaces';
interface WalletToNetworkTokenTicker {
  [key: string]: string;
}
interface TestnetToMainnetMapping {
  [key: string]: string;
}
interface TestnetTokenToMainnetTokenMapping {
  [key: string]: string;
}
const TRADE_SUPPORTED_NETWORKS = ['ethereum', 'base', 'stellar'];
const APP_CONFIG = {
  MINIMUM_SCREEN_WIDTH: 1024,
  BLOCKCHAINS_NOT_SUPPORTED_FOR_YIELD_BEARING_ASSET_DEPLOYMENT: [] as string[],
  YIELD_BEARING_ASSET_TICKERS_EXCLUSION_LIST: [] as string[],
  ALLOW_CROSS_CHAIN_YIELD_DEPLOYMENTS: true,
  TRADE_SUPPORTED_NETWORKS,
  TRADEABLE_TOKENS_SUPPORTED_NETWORKS: TRADE_SUPPORTED_NETWORKS,
  ASSET_TO_SELL_DEFAULT_PERCENTAGE: 0.25,
  ERROR_BOUNDARY_ILLUSTRATION_URL: `/images/svg/peace.svg`,
  SMALL_SCREEN_ILLUSTRATION_URL: `/images/svg/safe-vault.svg`,
  DISABLE_STRICT_MODE_IN_DEVELOPMENT: true,
  SHOW_NEWS: true,
  SHOW_ROADMAP_BUTTON: false,
  STUBS: {
    USE_WALLETS_STUB: false,
    USE_TRANSACTIONS_STUB: false,
    USE_TRADEABLE_TOKENS_STUB: false,
    WALLETS_RESPONSE_DELAY_MS: 5 * 1000,
    TRANSACTIONS_RESPONSE_DELAY_MS: 5 * 1000,
    TRADEABLE_TOKENS_RESPONSE_DELAY_MS: 2 * 1000,
  },
  LOCAL_CACHE: {
    DEFAULT_TTL: 24 * 60 * 60 * 1000,
    GAS_ESTIMATE_TTL: 10 * 1000,
  },
  THROTTLE: {
    WALLETS_THROTTLE_DURATION_IN_MS: 10 * 1000,
    WALLETS_DELAY_BEFORE_FETCH_IN_MS: 5 * 1000,
    TRANSACTIONS_THROTTLE_DURATION_IN_MS: 10 * 1000,
  },
  TIMING: {
    RESEARCH_DRAWER_LOADING_DELAY: {
      MIN_MS: 2000,
      MAX_MS: 5000,
    },
  },
  WALLETS_AND_TRANSACTIONS_REFRESH: {
    INITIAL_DELAY_IN_MS: 5 * 1000,
    MAX_ATTEMPTS: 4,
    BACKOFF_FACTOR: 1.5,
    JITTER_FACTOR: 0.2,
    TRANSACTION_REFRESH_TIMEOUT_IN_MS: 120 * 1000,
  },
  API_SERVICE: {
    BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    TOKEN_REFRESH_INTERVAL_MS: 45 * 60 * 1000,
    SESSION_RELOAD_DELAY_MS: 100,
    VISIBILITY_CHANGE_TOKEN_REFRESH_ENABLED: true,
  },
  WALLET: {
    SUPPORTED_BLOCKCHAIN_IDS: ['ethereum', 'base', 'stellar'],
  },
  MAPPINGS: {
    BLOCKCHAIN_ID_TO_BLOCKCHAIN_DISPLAY_NAME: {
      ethereum: `Ethereum`,
      base: `Base on Ethereum`,
      stellar: `Stellar`,
    },
  },
  COINBASE_APP_ID: '3b83c147-29a6-40c5-bc5c-75a0be8f8c8c',
  TRENDING_YIELD_BEARING_ASSETS_CARD: {
    NO_OF_ASSETS_TO_SHOW: 5,
    ASSETS: [`SPR`, `GST`, `AZR`, `OVR`, `WWL`],
  },
  BUY_TOKEN_DROPDOWN_FEATURED_TOKENS_COUNT: 5,
  ALL_YIELD_BEARING_ASSETS_CARD: {
    NO_OF_ASSETS_TO_SHOW: 500,
  },
  MOST_RECENT_TRANSACTIONS_CARD: {
    NO_OF_TRANSACTIONS_TO_SHOW: 10,
  },
  ALL_TRANSACTIONS_CARD: {
    NO_OF_TRANSACTIONS_TO_SHOW: 500,
  },
  PORTFOLIO_SHORT_LIST_NO_OF_ASSETS_TO_SHOW: 5,
  HISTORICAL_YIELD_DATA_TO_SHOW_IN_MONTHS: 6,
  YIELD_BEARING_ASSETS_DATA_FETCH_INTERVAL_IN_MS: 30 * 60 * 1000,
  TRANSACTIONS_DATA_FETCH_INTERVAL_IN_MS: 10 * 60 * 1000,
  USER_PORTFOLIO_ASSETS_DATA_FETCH_INTERVAL_IN_MS: 30 * 60 * 1000,
  WALLETS_DATA_FETCH_INTERVAL_IN_MS: 1 * 60 * 60 * 1000,
  DEFI_LLAMA_YIELD_DATA_FETCH_INTERVAL_IN_MS: 1 * 60 * 60 * 1000,
  DEFI_LLAMA_HISTORICAL_DATA_FETCH_INTERVAL_IN_MS: 1 * 24 * 60 * 60 * 1000,
  DEFI_LLAMA_PRICE_FETCH_INTERVAL_IN_MS: 10 * 60 * 1000,
  NO_OF_TIMES_TO_FETCH_ASSETS_AFTER_YIELD_DEPLOYMENT: 3,
  WALLET_BLOCKCHAIN_TO_NAME_MAPPING: {
    'SOL-DEVNET': 'Solana',
    'MATIC-AMOY': 'Polygon',
    'ETH-SEPOLIA': 'Ethereum',
  },
  TESTNET_TO_MAINNET_MAPPING: {
    'eth-sepolia': 'ethereum',
  } as TestnetToMainnetMapping,
  TESTNET_TOKEN_TO_MAINNET_TOKEN_MAPPING: {
    '0x08210f9170f89ab7658f0b5e3ff39b0e03c594d4':
      '0x1abaea1f7c830bd89acc67ec4af516284b1bc33c',
    '0x1c7d4b196cb0c7b01d743fbc6116a902379c7238':
      '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48',
  } as TestnetTokenToMainnetTokenMapping,
  TOKEN_IMAGE_PATHS: (tokenSymbolLowercase: string) => [
    `/images/crypto-logos/svg/color/${tokenSymbolLowercase}.svg`,
    `/images/crypto-logos/svg/color/generic.svg`,
  ],
  DEFAULT_TOKEN_ICON_IMAGE_PATH: `/images/crypto-logos/svg/color/generic.svg`,
  RATING_EMOJIS: ['💩', '👎', '🎲', '👍', '🔥'],
  ANIMATION_SETTINGS: {
    SLIDE_FROM: {
      LEFT: {
        initial: { x: '-5%', opacity: 0 },
        open: { x: '0%', opacity: 1 },
        closed: { x: '-5%', opacity: 0 },
      },
      RIGHT: {
        initial: { x: '5%', opacity: 0 },
        open: { x: '0%', opacity: 1 },
        closed: { x: '5%', opacity: 0 },
      },
      TOP: {
        initial: { y: '-5%', opacity: 0 },
        open: { y: '0%', opacity: 1 },
        closed: { y: '-5%', opacity: 0 },
      },
      BOTTOM: {
        initial: { y: '5%', opacity: 0 },
        open: { y: '0%', opacity: 1 },
        closed: { y: '5%', opacity: 0 },
      },
    },
    COMMON: {
      type: 'spring',
      ease: 'easeInOut',
      stiffness: 150,
      damping: 25,
      duration: 300,
    },
    TOAST: {
      OPEN_DURATION: 5000,
    },
    DROPDOWN: {
      initial: { y: '-5%', opacity: 0 },
      open: { y: '0%', opacity: 1 },
      closed: { y: '-5%', opacity: 0, transition: { duration: 0 } },
      transition: {
        type: 'spring',
        stiffness: 250,
        damping: 20,
        duration: 200,
      },
    },
    TOOLTIP: {
      initial: { y: '-10%', opacity: 0 },
      open: { y: '0%', opacity: 1 },
      closed: { y: '-5%', opacity: 0, transition: { duration: 0 } },
      transition: {
        type: 'spring',
        stiffness: 200,
        damping: 20,
        duration: 150,
      },
    },
  },
  PORTFOLIO_TOKEN_COLORS: [
    'bg-blue-500',
    'bg-pink-500',
    'bg-green-500',
    'bg-orange-500',
    'bg-purple-500',
    'bg-yellow-500',
    'bg-teal-500',
    'bg-red-500',
    'bg-indigo-500',
    'bg-cyan-500',
    'bg-lime-500',
    'bg-fuchsia-500',
    'bg-amber-500',
    'bg-rose-500',
    'bg-violet-500',
    'bg-sky-500',
    'bg-emerald-500',
    'bg-orange-500',
    'bg-lime-500',
    'bg-blue-600',
    'bg-pink-600',
    'bg-green-600',
    'bg-orange-600',
    'bg-purple-600',
    'bg-yellow-600',
    'bg-teal-600',
    'bg-red-600',
    'bg-indigo-600',
    'bg-cyan-600',
    'bg-lime-600',
    'bg-fuchsia-600',
    'bg-amber-600',
    'bg-rose-600',
    'bg-violet-600',
    'bg-sky-600',
    'bg-emerald-600',
    'bg-orange-600',
    'bg-lime-600',
    'bg-blue-700',
    'bg-pink-700',
    'bg-green-700',
    'bg-orange-700',
    'bg-purple-700',
    'bg-yellow-700',
    'bg-teal-700',
    'bg-red-700',
    'bg-indigo-700',
    'bg-cyan-700',
    'bg-sky-700',
    'bg-violet-700',
  ],
  TOKEN_COLORS: [
    'bg-red-500',
    'bg-blue-500',
    'bg-yellow-500',
    'bg-green-500',
    'bg-pink-500',
    'bg-cyan-500',
    'bg-orange-500',
    'bg-teal-500',
    'bg-purple-500',
    'bg-lime-500',
    'bg-indigo-500',
    'bg-amber-500',
    'bg-fuchsia-500',
    'bg-sky-500',
    'bg-rose-500',
    'bg-violet-500',
    'bg-emerald-500',
    'bg-blue-600',
    'bg-red-600',
    'bg-green-600',
    'bg-yellow-600',
    'bg-pink-600',
    'bg-orange-600',
    'bg-teal-600',
    'bg-cyan-600',
    'bg-purple-600',
    'bg-lime-600',
    'bg-indigo-600',
    'bg-amber-600',
    'bg-fuchsia-600',
    'bg-sky-600',
    'bg-rose-600',
    'bg-violet-600',
    'bg-emerald-600',
    'bg-blue-700',
    'bg-red-700',
    'bg-green-700',
    'bg-yellow-700',
    'bg-pink-700',
    'bg-orange-700',
    'bg-teal-700',
    'bg-cyan-700',
    'bg-purple-700',
    'bg-lime-700',
    'bg-indigo-700',
    'bg-amber-700',
    'bg-fuchsia-700',
    'bg-sky-700',
    'bg-rose-700',
    'bg-violet-700',
  ],
  YIELD_BEARING_ASSET_TICKER_TO_ICON_NAME_MAPPING: {
    MMB: 'mmb',
  },
  STYLES: {
    MAIN_PADDING: '-md',
    MAIN_GAP: '-sm',
    SUB_GAP: '-xs',
    MAIN_ROUNDEDNESS: '-2xs',
    MAIN_BACKDROP_BLUR: '-3xl',
    ALTERNATIVE_BACKDROP_BLUR: '-lg',
    WHOLESTEP_INCREMENT: '-wholestep',
    HALFSTEP_INCREMENT: '-halfstep',
    QUARTERSTEP_INCREMENT: '-quarterstep',
    EIGHTHSTEP_INCREMENT: '-eighthstep',
    WHOLESTEP_DECREMENT: '-wholestep-dec',
    HALFSTEP_DECREMENT: '-halfstep-dec',
    QUARTERSTEP_DECREMENT: '-quarterstep-dec',
    EIGHTHSTEP_DECREMENT: '-eighthstep-dec',
    TEXT: {
      BIG_HEADING: 'title3' as TextVariants['size'],
      BIG_SUBHEADING: 'subheading' as TextVariants['size'],
      BIG_HEADING_COLOR: 'primary' as TextVariants['color'],
      BIG_SUBHEADING_COLOR: 'secondary' as TextVariants['color'],
      SMALL_HEADING: 'heading' as TextVariants['size'],
      SMALL_SUBHEADING: 'subheading' as TextVariants['size'],
      SMALL_CAPTION: 'caption' as TextVariants['size'],
      SMALL_HEADING_COLOR: 'primary' as TextVariants['color'],
      SMALL_SUBHEADING_COLOR: 'tertiary' as TextVariants['color'],
      SMALL_CAPTION_COLOR: 'tertiary' as TextVariants['color'],
      BODY: 'body' as TextVariants['size'],
      BODY_SUBCONTENT: 'caption' as TextVariants['size'],
      BODY_COLOR: 'secondary' as TextVariants['color'],
      BODY_SUBCONTENT_COLOR: 'tertiary' as TextVariants['color'],
    },
  },
};
export default APP_CONFIG;
